import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { dataQualityService } from '../../api/services';
import { DataQualityReport, DataQualitySeverity } from '../../types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Qualité de données (TF-029 → TF-034) : scanner d'anomalies déterministes sur les
 * données réelles de la société (clients/fournisseurs/articles/documents/comptabilité).
 * Chaque anomalie pointe vers son écran de correction. Affiche aussi le score de santé
 * et la couverture des facteurs carbone (TF-037).
 */
export function QualiteDonnees() {
  const [report, setReport] = useState<DataQualityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState<DataQualitySeverity | 'TOUTES'>('TOUTES');

  const load = () => {
    setLoading(true);
    dataQualityService.getReport().then(setReport).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const issues = useMemo(() => {
    if (!report) return [];
    return filtre === 'TOUTES' ? report.issues : report.issues.filter((i) => i.severite === filtre);
  }, [report, filtre]);

  if (loading) return <LoadingSpinner />;
  if (!report) return <div className="card">Impossible de charger le rapport qualité.</div>;

  const s = report.summary;
  const scoreColor = s.scoreSante >= 80 ? 'text-green-600'
    : s.scoreSante >= 50 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Qualité des données</h1>
          <p className="page-subtitle">
            Anomalies détectées sur vos données réelles, classées par criticité — corrigez en un clic
          </p>
        </div>
        <button onClick={load} className="btn-secondary">Relancer le scan</button>
      </div>

      {/* Synthèse */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="card">
          <p className="text-xs text-gray-500">Score de santé</p>
          <p className={`text-2xl font-bold ${scoreColor}`}>{s.scoreSante}/100</p>
        </div>
        <button onClick={() => setFiltre('CRITIQUE')} className="card text-left hover:ring-2 hover:ring-red-200">
          <p className="text-xs text-gray-500">Critiques</p>
          <p className="text-2xl font-bold text-red-600">{s.critiques}</p>
        </button>
        <button onClick={() => setFiltre('IMPORTANT')} className="card text-left hover:ring-2 hover:ring-amber-200">
          <p className="text-xs text-gray-500">Importantes</p>
          <p className="text-2xl font-bold text-amber-600">{s.importants}</p>
        </button>
        <button onClick={() => setFiltre('MINEUR')} className="card text-left hover:ring-2 hover:ring-gray-200">
          <p className="text-xs text-gray-500">Mineures</p>
          <p className="text-2xl font-bold text-gray-600">{s.mineurs}</p>
        </button>
        <div className="card">
          <p className="text-xs text-gray-500">Couverture carbone</p>
          <p className="text-2xl font-bold text-emerald-600">{s.couvertureFacteurCarbone}%</p>
        </div>
      </div>

      {/* Filtre */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500">Filtrer :</span>
        {(['TOUTES', 'CRITIQUE', 'IMPORTANT', 'MINEUR'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFiltre(f)}
            className={`px-3 py-1 rounded-full border ${
              filtre === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            {f === 'TOUTES' ? 'Toutes' : f.charAt(0) + f.slice(1).toLowerCase()}
          </button>
        ))}
        <span className="ml-auto text-gray-400">{issues.length} anomalie(s)</span>
      </div>

      {/* Liste */}
      {issues.length === 0 ? (
        <div className="card text-center text-gray-500 py-10">
          Aucune anomalie pour ce filtre. Vos données sont saines.
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Sévérité</th>
                <th className="px-4 py-2">Module</th>
                <th className="px-4 py-2">Anomalie</th>
                <th className="px-4 py-2">Détail</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {issues.map((i, idx) => (
                <tr key={`${i.code}-${i.entiteId}-${idx}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2"><SeverityBadge severite={i.severite} /></td>
                  <td className="px-4 py-2 text-gray-600">{i.module}</td>
                  <td className="px-4 py-2 font-medium text-gray-800">{i.libelle}</td>
                  <td className="px-4 py-2 text-gray-500">{i.detail}</td>
                  <td className="px-4 py-2 text-right">
                    <Link to={i.lien} className="text-blue-600 hover:underline whitespace-nowrap">Corriger →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SeverityBadge({ severite }: { severite: DataQualitySeverity }) {
  const map: Record<DataQualitySeverity, string> = {
    CRITIQUE: 'bg-red-100 text-red-700',
    IMPORTANT: 'bg-amber-100 text-amber-700',
    MINEUR: 'bg-gray-100 text-gray-600',
  };
  const label = severite.charAt(0) + severite.slice(1).toLowerCase();
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[severite]}`}>{label}</span>;
}

export default QualiteDonnees;
